import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Popconfirm } from "antd";
import { aiImageLabelToQuality, aiImageModelToLabel, aiImageQualityToLabel, deleteAiImageTaskApi, generateAiImageApi, getAiImageBatchApi, getAiImagePromptStartersApi, getAiImageTaskApi, getAiImageTasksApi, getAiImageTemplateApi, getAiImageTemplatesApi, previewAiImagePointApi, type AiImageQuality, type AiImageTask, type AiImageTemplate } from "../../service/aiImage";
import { triggerMembershipRefresh } from "../../service/membership";
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { downloadFileSmart } from "../../utils/batchVideoDownload";
import { AppToast, type ToastType } from "../../components/common/AlertMessage";
import "./AiImage.scss";

type SelectKey = "ratio" | "resolution" | "quality" | "count" | "model";
type TabKey = "templates" | "works";

interface SelectOption {
  value: string;
  cost?: number;
  count?: number;
}

interface WorkItem {
  id: string;
  prompt: string;
  params: Record<SelectKey, string>;
  cost: number;
  pending?: boolean;
  failed?: boolean;
  image?: string;
  createdAt?: string;
  taskId?: number;
  /** 原图宽高比（如 "2:3" / "16:9"），用于 masonry 卡片按原比例展示 */
  aspectRatio: string;
  /** 原任务携带的参考图 URL；为空表示无参考图，回填时要把当前已传的参考图清掉 */
  referenceImageUrl?: string | null;
}

const SELECT_GROUPS: Array<{
  key: SelectKey;
  icon: string;
  defaultValue: string;
  modelButton?: boolean;
  options: SelectOption[];
}> = [
  {
    key: "ratio",
    icon: "ratio",
    defaultValue: "9:16",
    options: ["1:1", "4:3", "3:4", "2:3", "16:9", "9:16"].map((value) => ({ value })),
  },
  {
    key: "resolution",
    icon: "resolution",
    defaultValue: "1K",
    options: [
      { value: "1K", cost: 4 },
      { value: "2K", cost: 8 },
      { value: "4K", cost: 14 },
    ],
  },
  {
    key: "quality",
    icon: "quality",
    defaultValue: "标准",
    options: [
      { value: "标准", cost: 1 },
      { value: "精细", cost: 2 },
      { value: "专业", cost: 3 },
    ],
  },
  {
    key: "count",
    icon: "count",
    defaultValue: "1 张",
    options: [
      { value: "1 张", count: 1 },
      { value: "2 张", count: 2 },
      { value: "4 张", count: 4 },
    ],
  },
  {
    key: "model",
    icon: "model",
    defaultValue: "GPT-Image-2 (Stable)",
    modelButton: true,
    options: [
      { value: "GPT-Image-2 (Stable)", cost: 1 },
    ],
  },
];

type IdeaCard = { index: string; tag: string; title: string; summary: string; prompt: string };
type TemplateCard = { id: string; category: string; overlay: string; title: string; meta: string; alt: string; image: string; fallbackClass: string; aspectRatio: string };

/** 画廊列数（桌面端 4 列 masonry） */
const GALLERY_COLUMN_COUNT = 4;

/** 将比例字符串（如 "16:9"）转换为 CSS aspect-ratio 值（如 "16 / 9"），非法值兜底 1:1 */
const ratioToCss = (ratio: string): string => {
  const safe = ratio && ratio.includes(":") ? ratio : "1:1";
  const [w, h] = safe.split(":");
  return `${w || 1} / ${h || 1}`;
};

/**
 * 计算卡片高度分数（用于 masonry 列分配）
 * 分数越高占用列空间越多，分配到当前最矮列以均衡各列高度
 */
const heightScore = (ratio: string): number => {
  const safe = ratio && ratio.includes(":") ? ratio : "1:1";
  const parts = safe.split(":").map(Number);
  const w = parts[0] || 1;
  const h = parts[1] || 1;
  return h / w;
};

/** 将列表按高度分数分配到 N 列（masonry，分配到当前最矮列） */
const splitColumns = <T,>(items: T[], scoreOf: (item: T) => number, count: number): T[][] => {
  const columns: T[][] = Array.from({ length: count }, () => []);
  const scores = Array(count).fill(0);
  items.forEach((item) => {
    const target = scores.indexOf(Math.min(...scores));
    columns[target].push(item);
    scores[target] += scoreOf(item);
  });
  return columns;
};

const templateFilterKey = (template: AiImageTemplate): string => {
  const group = String(template.groupCategory || template.category || "");
  if (/人物/.test(group)) return "editorial";
  if (/视觉|海报/.test(group)) return "campaign";
  if (/品牌|菜单/.test(group)) return "brand";
  return group || "all";
};

const toTemplateCard = (template: AiImageTemplate) => ({
  id: String(template.id), category: templateFilterKey(template), overlay: template.badge || template.templateType || "AI TEMPLATE",
  title: template.title || "", meta: template.category || template.groupCategory || "", alt: template.title || "",
  image: template.coverImageUrl || "", fallbackClass: "",
  aspectRatio: template.aspectRatio || "2:3",
});

const toIdeaCard = (template: AiImageTemplate, index: number) => ({
  index: String(index + 1).padStart(2, "0"), tag: template.category || template.groupCategory || "PROMPT",
  title: template.title || "", summary: template.description || "", prompt: template.prompt || "",
});

const toWorkItems = (task: AiImageTask): WorkItem[] => {
  const params: Record<SelectKey, string> = {
    ratio: task.aspectRatio || "16:9", resolution: task.resolution || "2K", quality: task.quality || "LOW",
    count: String(task.imageCount || task.imageUrls?.length || 1) + " 张", model: aiImageModelToLabel(task.model),
  };
  const images = task.imageUrls?.length ? task.imageUrls : [""];
  const aspectRatio = task.aspectRatio || "16:9";
  return images.map((image, index) => ({
    id: String(task.taskId) + "-" + index, taskId: task.taskId, prompt: task.prompt || "", params, cost: 0,
    pending: task.status === "processing", failed: String(task.status).toLowerCase() === "failed", image, createdAt: task.createTime,
    aspectRatio,
    referenceImageUrl: task.referenceImageUrl ?? null,
  }));
};

function SvgIcon({ name }: { name: string }) {
  switch (name) {
    case "image":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="3" y="4" width="15" height="16" rx="2" /><circle cx="8" cy="9" r="1.5" /><path d="m3 16 4-4 4 4 2-2 5 5M21 7v6M18 10h6" /></svg>;
    case "close":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6 6 18" /></svg>;
    case "ratio":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="6" width="18" height="12" rx="2" /></svg>;
    case "resolution":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" /></svg>;
    case "quality":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 18a8 8 0 1 1 16 0M12 14l4-4" /></svg>;
    case "count":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="m12 3 9 5-9 5-9-5ZM3 12l9 5 9-5M3 16l9 5 9-5" /></svg>;
    case "model":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="6" y="6" width="12" height="12" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" /></svg>;
    case "chevron":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="m7 10 5 5 5-5" /></svg>;
    case "coins":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><ellipse cx="9" cy="6" rx="6" ry="3" /><path d="M3 6v4c0 1.7 2.7 3 6 3s6-1.3 6-3V6M3 10v4c0 1.7 2.7 3 6 3 1 0 2-.1 2.8-.4M15 10c3.3 0 6 1.3 6 3s-2.7 3-6 3-6-1.3-6-3M9 13v4c0 1.7 2.7 3 6 3s6-1.3 6-3v-4" /></svg>;
    case "arrow":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 12h14M14 7l5 5-5 5" /></svg>;
    case "external":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M7 17 17 7M8 7h9v9" /></svg>;
    case "sparkles":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2ZM6 14l.8 2.2L9 17l-2.2.8L6 20l-.8-2.2L3 17l2.2-.8Z" /></svg>;
    case "eye":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2.5 12s3.4-6 9.5-6 9.5 6 9.5 6-3.4 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></svg>;
    case "download":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>;
    case "trash":
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" /></svg>;
    case "more-horizontal":
      return <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>;
    default:
      return null;
  }
}

function getOption(key: SelectKey, value: string): SelectOption | undefined {
  return SELECT_GROUPS.find((group) => group.key === key)?.options.find((option) => option.value === value);
}

function getCostFactor(key: SelectKey, value: string): number {
  const option = getOption(key, value);
  return key === "count" ? option?.count ?? 1 : option?.cost ?? 1;
}

function makeWorkTitle(text: string): string {
  const clean = text.replace(/\s+/g, " ").replace(/[，。！？,.!?].*$/, "").trim();
  return clean.length > 18 ? `${clean.slice(0, 18)}...` : clean;
}

/**
 * 把 http URL 强转 https，避免某些代理 / 混部下发时浏览器仍按 http 加载
 * （导致 mixed-content / CORS / fetch 被拦）。已为 https 的不动。
 */
function ensureHttpsUrl(url: string): string {
  return url.replace(/^http:\/\//i, "https://");
}

/**
 * 把远端参考图 URL 当作「上传」：先转 https，再 fetch 拿到 Blob，包成 File。
 * 这样后端拿到的就是用户上传的 File，行为跟用户主动上传完全一致，
 * 不会再需要独立的 referenceImageUrl 字段。
 *
 * 失败（网络 / CORS / 非图片）返回 null，由调用方决定是否降级（只展示预览，不上传）。
 */
async function fetchReferenceAsFile(url: string): Promise<File | null> {
  if (!url || !/^https?:\/\//i.test(url)) return null;
  try {
    const response = await fetch(ensureHttpsUrl(url), { mode: "cors", credentials: "omit" });
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith("image/")) return null;
    const pathname = (() => {
      try { return new URL(url).pathname; } catch { return ""; }
    })();
    const guessedName = pathname.split("/").pop() || "reference";
    const ext = blob.type.split("/")[1] || "jpg";
    const fileName = /\.[a-z0-9]+$/i.test(guessedName) ? guessedName : `${guessedName}.${ext}`;
    return new File([blob], fileName, { type: blob.type });
  } catch (error) {
    console.warn("[aiImage] 参考图回填转 File 失败", error);
    return null;
  }
}

export const AiImage: FC = () => {
  const [prompt, setPrompt] = useState("");
  const [referencePreview, setReferencePreview] = useState("");
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [selected, setSelected] = useState<Record<SelectKey, string>>({
    ratio: "9:16",
    resolution: "1K",
    quality: "标准",
    count: "1 张",
    model: "GPT-Image-2 (Stable)",
  });
  const [openMenu, setOpenMenu] = useState<SelectKey | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("templates");
  const [activeFilter, setActiveFilter] = useState("all");
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [deletingTaskIds, setDeletingTaskIds] = useState<number[]>([]);
  const [openWorkMoreMenuId, setOpenWorkMoreMenuId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateCard[]>([]);
  const [ideas, setIdeas] = useState<IdeaCard[]>([]);
  const [toastText, setToastText] = useState("提示词已填入");
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<ToastType>("info");
  const [generating, setGenerating] = useState(false);
  const [estimatedPoint, setEstimatedPoint] = useState<number | null>(null);
  const [pointLoading, setPointLoading] = useState(false);
  const [hiddenTemplateImages, setHiddenTemplateImages] = useState<Record<string, boolean>>({});
  /** 增量渲染：每次加载的条数 */
  const DISPLAY_STEP = 8;
  const [displayCount, setDisplayCount] = useState(DISPLAY_STEP);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const composerRef = useRef<HTMLElement | null>(null);
  const galleryRef = useRef<HTMLElement | null>(null);
  const taskPollTimerRef = useRef<number | null>(null);
  const navigate = useNavigate();

  const notify = useCallback((message: string, type: ToastType = "info") => {
    setToastText(message);
    setToastType(type);
    setToastVisible(true);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToastVisible(false), type === "error" ? 4200 : 2200);
  }, []);

  const loadWorks = useCallback(async (silent = false) => {
    try {
      const tasks = await getAiImageTasksApi();
      if (!silent) {
        // 首次加载：可显示 loading 态；静默刷新时跳过
      }
      setWorks(tasks.flatMap(toWorkItems));
    } catch {
      // 静默失败，不打扰用户
    }
  }, []);

  const mergeTask = useCallback((task: AiImageTask) => {
    const taskWorks = toWorkItems(task);
    setWorks((current) => [...taskWorks, ...current.filter((work) => work.taskId !== task.taskId)].slice(0, 50));
  }, []);

  const stopTaskPolling = useCallback(() => {
    if (taskPollTimerRef.current !== null) {
      window.clearTimeout(taskPollTimerRef.current);
      taskPollTimerRef.current = null;
    }
  }, []);

  const pollGeneratedTask = useCallback(async (taskId: number) => {
    try {
      // 轮询 /ai-image/batches/{batchId}（batchId === 第一次返回的 taskId）
      const batch = await getAiImageBatchApi(taskId);
      const status = String(batch.status || "").toLowerCase();
      if (status === "success" || status === "failed") {
        // 轮询成功 → 静默调用 /ai-image/tasks 刷新列表，不闪页面
        await loadWorks(true);
        stopTaskPolling();
        setGenerating(false);
        triggerMembershipRefresh();
        if (status === "success") notify("图片已生成，并保存到「我的作品」", "success");
        else notify("生成失败，请稍后重试", "error");
        return;
      }
    } catch {
      // 保留轮询：批次可能还没准备好
    }
    stopTaskPolling();
    taskPollTimerRef.current = window.setTimeout(() => void pollGeneratedTask(taskId), 10000);
  }, [loadWorks, notify, stopTaskPolling]);

  useEffect(() => {
    let active = true;
    void Promise.allSettled([
      getAiImageTemplatesApi(),
      getAiImagePromptStartersApi(),
      loadWorks(true),
    ]).then((results) => {
      if (!active) return;
      const [templateResult, starterResult] = results;
      if (templateResult.status === "fulfilled") setTemplates(templateResult.value.map(toTemplateCard));
      if (starterResult.status === "fulfilled") setIdeas(starterResult.value.map(toIdeaCard));
    });
    return () => {
      active = false;
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      if (taskPollTimerRef.current !== null) window.clearTimeout(taskPollTimerRef.current);
    };
  }, [loadWorks]);

  useEffect(() => {
    works.slice(0, 12).forEach((work) => {
      if (!work.image) return;
      const image = new Image();
      image.decoding = "async";
      image.src = work.image;
    });
  }, [works]);

  useEffect(() => {
    const closeMenus = () => setOpenMenu(null);
    document.addEventListener("click", closeMenus);
    return () => document.removeEventListener("click", closeMenus);
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get("tab") === "works") setActiveTab("works");
    if (!query.has("fromTemplate") && !query.has("fromWork")) return;

    try {
      const saved = JSON.parse(localStorage.getItem("ai_image_template_apply") || "null");
      if (!saved?.prompt) return;

      setPrompt(String(saved.prompt));
      setSelected((current) => ({ ...current, ...(saved.params || {}) }));
      // 回填参考图：URL 非空 → 当作用户重新上传一次（http→https 后 fetch 转 File），
// 跟 reuseWork 路径行为一致；URL 为空时清掉旧的预览避免串
      const refUrl = typeof saved.referenceImageUrl === "string" ? saved.referenceImageUrl : "";
      if (refUrl) {
        setReferencePreview(refUrl);
        if (fileInputRef.current) fileInputRef.current.value = "";
        // useEffect 回调不是 async，IIFE 异步拿 File
        void fetchReferenceAsFile(refUrl).then((file) => {
          setReferenceFile(file);
          if (!file) notify("参考图预览已载入，但本地准备文件失败，生图时不会带参考图");
        });
      }
      setActiveTab(query.has("fromWork") ? "works" : "templates");
      localStorage.removeItem("ai_image_template_apply");
      window.history.replaceState(null, "", window.location.pathname);
      notify("模板提示词与生成参数已带入工作台");
    } catch {
      localStorage.removeItem("ai_image_template_apply");
    }
  }, [notify]);

  const visibleTemplates = useMemo(
    () => templates.filter((template) => activeFilter === "all" || template.category === activeFilter),
    [activeFilter, templates]
  );

  /** 模板 masonry 列：按 ratio 高度分数分配到当前最矮列 */
  const templateColumns = useMemo(
    () => splitColumns(visibleTemplates, (tpl) => heightScore(tpl.aspectRatio), GALLERY_COLUMN_COUNT),
    [visibleTemplates]
  );

  /** 积分预估：分辨率 / 画质 / 数量变更后 ~500ms 防抖调用 POST /ai-image/point-preview */
  useEffect(() => {
    if (!prompt.trim()) {
      setEstimatedPoint(null);
      setPointLoading(false);
      return;
    }
    const quality = aiImageLabelToQuality(selected.quality);
    const imageCount = Number(getCostFactor("count", selected.count)) || 1;
    if (!selected.resolution || !quality) return;
    setPointLoading(true);
    const timer = window.setTimeout(() => {
      void previewAiImagePointApi({ resolution: selected.resolution, quality, imageCount } as {
        resolution: string; quality: AiImageQuality; imageCount: number;
      })
        .then((res) => setEstimatedPoint(res?.estimatedPoint ?? null))
        .catch(() => setEstimatedPoint(null))
        .finally(() => setPointLoading(false));
    }, 500);
    return () => window.clearTimeout(timer);
  }, [prompt, selected.resolution, selected.quality, selected.count]);

  const handleReferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setReferencePreview(String(reader.result || ""));
      setReferenceFile(file);
      notify("参考图已添加");
    };
    reader.readAsDataURL(file);
  };

  const clearReference = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setReferencePreview("");
    setReferenceFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const selectOption = (key: SelectKey, value: string) => {
    setSelected((current) => ({ ...current, [key]: value }));
    setOpenMenu(null);
  };

  const reuseWork = async (work: WorkItem) => {
    let next = work;
    try {
      if (work.taskId) next = toWorkItems(await getAiImageTaskApi(work.taskId))[0] || work;
      setPrompt(next.prompt);
      // params 里存的是后端字段值（LOW/HIGH/EXTREME），selected 需要中文标签供 UI 显示
      setSelected({ ...next.params, quality: aiImageQualityToLabel(next.params.quality) });
      // 参考图回填：URL 非空 → 当作用户重新上传一次（http→https 后 fetch 转 File），
// 这样走 image 字段上传，跟用户本地上传行为一致；URL 为空 → 把当前已传的参考图也清掉，避免旧的参考图跟着新任务继续生效
      const refUrl = next.referenceImageUrl || "";
      if (refUrl) {
        setReferencePreview(refUrl);
        if (fileInputRef.current) fileInputRef.current.value = "";
        const file = await fetchReferenceAsFile(refUrl);
        setReferenceFile(file);
        if (!file) notify("参考图预览已载入，但本地准备文件失败，生图时不会带参考图");
      } else {
        setReferencePreview("");
        setReferenceFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => composerRef.current?.querySelector("textarea")?.focus(), 350);
      notify("提示词与生成参数已重新载入");
    } catch {
      notify("作品配置加载失败");
    }
  };

  const handleDeleteWork = useCallback(async (work: WorkItem) => {
    const taskId = work.taskId;
    if (!taskId || deletingTaskIds.includes(taskId)) return;
    setDeletingTaskIds((prev) => [...prev, taskId]);
    try {
      const ok = await deleteAiImageTaskApi(taskId);
      if (ok) {
        setWorks((prev) => prev.filter((item) => item.taskId !== taskId));
        notify("作品已删除", "success");
      } else {
        notify("删除失败，请稍后重试", "error");
      }
    } catch (error) {
      console.error("删除 AI 生图作品失败:", error);
      notify("删除失败，请稍后重试", "error");
    } finally {
      setDeletingTaskIds((prev) => prev.filter((id) => id !== taskId));
    }
  }, [deletingTaskIds, notify]);

  const applyTemplate = async (template: { id: string }) => {
    const id = Number(template.id);
    if (!Number.isFinite(id)) return;
    try {
      const full = await getAiImageTemplateApi(id);
      setPrompt(full.prompt || "");
      setSelected({
        ratio: full.aspectRatio || "16:9",
        resolution: full.resolution || "2K",
        quality: aiImageQualityToLabel(full.quality),
        count: String(full.imageCount || 1) + " 张",
        model: aiImageModelToLabel(full.model),
      });
      composerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => composerRef.current?.querySelector("textarea")?.focus(), 300);
      notify("模板配置已应用，可继续修改");
    } catch {
      notify("模板配置加载失败");
    }
  };

  const hasUnfinishedTask = useMemo(() => works.some((work) => work.pending), [works]);
  const generationLocked = generating || hasUnfinishedTask;

  useEffect(() => {
    if (!generating && !hasUnfinishedTask) return;

    // 兜底：万一 generate 之后的 taskId 轮询中断 / 页面被刷新过，
    // 这里额外做一次轮询：找到第一条 processing 的作品，按其 taskId 轮询。
    const processingBatchId = works.find((work) => work.pending && work.taskId)?.taskId;
    if (processingBatchId) {
      stopTaskPolling();
      taskPollTimerRef.current = window.setTimeout(() => void pollGeneratedTask(processingBatchId), 800);
    }
    return () => stopTaskPolling();
  }, [generating, hasUnfinishedTask, loadWorks, pollGeneratedTask, stopTaskPolling, works]);

  /** 增量渲染：IntersectionObserver 监听到达底部时加载更多 */
  useEffect(() => {
    setDisplayCount(DISPLAY_STEP);
  }, [works.length]);

  // 点击作品卡三点菜单外部区域时收起
  useEffect(() => {
    if (!openWorkMoreMenuId) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target.closest(".work-more-wrap")) return;
      setOpenWorkMoreMenuId(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openWorkMoreMenuId]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setDisplayCount((prev) => Math.min(prev + DISPLAY_STEP, works.length));
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [works.length]);

  const visibleWorks = useMemo(() => works.slice(0, displayCount), [works, displayCount]);

  /** 作品 masonry 列：按 ratio 高度分数分配到当前最矮列 */
  const workColumns = useMemo(
    () => splitColumns(visibleWorks, (work) => heightScore(work.aspectRatio), GALLERY_COLUMN_COUNT),
    [visibleWorks]
  );

  const generate = async () => {
    if (generationLocked) {
      notify("当前有未完成的生图任务，任务结束后可继续生图");
      return;
    }
    const requestPrompt = prompt.trim();
    if (!requestPrompt) {
      composerRef.current?.querySelector("textarea")?.focus();
      notify("请先输入画面描述或选择一条灵感提示词");
      return;
    }
    const count = getCostFactor("count", selected.count);
    stopTaskPolling();
    setGenerating(true);
    setActiveTab("works");
    window.setTimeout(() => triggerMembershipRefresh(), 2000);
    window.setTimeout(() => galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    try {
      const result = await generateAiImageApi({
        prompt: requestPrompt,
        aspectRatio: selected.ratio,
        resolution: selected.resolution,
        quality: aiImageLabelToQuality(selected.quality),
        imageCount: count,
        image: referenceFile,
      });
      if (result.status === "failed") throw new Error("生成失败");
      // 立即添加"正在生成"的占位卡片
      const params: Record<SelectKey, string> = {
        ratio: selected.ratio,
        resolution: selected.resolution,
        quality: aiImageLabelToQuality(selected.quality),
        count: selected.count,
        model: "",
      };
      const placeholderWorks: WorkItem[] = Array.from({ length: count }, (_, index) => ({
        id: String(result.taskId) + "-" + index,
        taskId: result.taskId,
        prompt: requestPrompt,
        params,
        cost: 0,
        pending: true,
        failed: false,
        image: "",
        createdAt: new Date().toISOString(),
        aspectRatio: selected.ratio,
      }));
      setWorks((prev) => [...placeholderWorks, ...prev]);
      void pollGeneratedTask(result.taskId);
    } catch (error) {
      setGenerating(false);
      notify(error instanceof Error && error.message ? error.message : "生成失败，请稍后重试", "error");
    }
  };

  const galleryNote = activeTab === "templates" ? "悬停预览 · 进入详情查看参数" : "结果自动保存 · 悬停可复用提示词";

  return (
    <div className="ai-image-page">
      <div className="ai-image-scroll">
        <div className="voice-shell">
          <section className="studio-hero">
            <div>
              <div className="section-kicker">Image Studio</div>
              <h1>AI 生图</h1>
              <p>用AI还原你脑海中的画面</p>
            </div>
          </section>

          <section className="composer" aria-label="AI 图片生成控制台" ref={composerRef}>
            <div className="composer-main">
              <label className={`reference${referencePreview ? " has-image" : ""}`} title="上传参考图">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleReferenceChange} />
                {referencePreview ? <img className="reference-preview" src={referencePreview} alt="参考图预览" /> : null}
                <span className="reference-content">
                  <SvgIcon name="image" />
                  <span>参考图</span>
                </span>
                <button className="reference-remove" type="button" aria-label="移除参考图" onClick={clearReference}>
                  <SvgIcon name="close" />
                </button>
              </label>
              <div className="prompt-wrap">
                <textarea
                  className="prompt"
                  maxLength={5000}
                  spellCheck={false}
                  placeholder="输入你想生成的画面.."
                  aria-label="图片提示词"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                />
                <div className="prompt-meta"><span>{prompt.length}</span>/5000</div>
              </div>
            </div>
            <div className="composer-toolbar">
              <div className="selectors" aria-label="生成参数">
                {SELECT_GROUPS.map((group) => (
                  <div className="select-wrap" data-key={group.key} key={group.key}>
                    <button
                      className={`select-button${group.modelButton ? " model-button" : ""}`}
                      type="button"
                      aria-expanded={openMenu === group.key}
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenMenu(openMenu === group.key ? null : group.key);
                      }}
                    >
                      <SvgIcon name={group.icon} />
                      <span>{selected[group.key]}</span>
                      <SvgIcon name="chevron" />
                    </button>
                    <div className={`select-menu${openMenu === group.key ? " open" : ""}`}>
                      {group.options.map((option) => (
                        <button
                          className={selected[group.key] === option.value ? "selected" : ""}
                          data-value={option.value}
                          type="button"
                          key={option.value}
                          onClick={(event) => {
                            event.stopPropagation();
                            selectOption(group.key, option.value);
                          }}
                        >
                          {option.value}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="actions">
                <span
                  className={`cost-pill${prompt.trim() && estimatedPoint !== null ? "" : " is-hidden"}`}
                  title="按当前参数预估，实际以扣减为准"
                >
                  <CoinsEstimateIcon />
                  <span>
                    预计消耗 <span className="cost-point">{estimatedPoint}</span> 积分
                  </span>
                </span>
                <button className={`generate-button${generating ? " loading" : ""}${generationLocked ? " is-disabled" : ""}`} type="button" aria-label="生成图片" title="生成图片" aria-disabled={generationLocked} onClick={generate}>
                  <SvgIcon name="arrow" />
                </button>
              </div>
            </div>
          </section>

          <section className="inspiration-block" aria-labelledby="inspirationTitle">
            <header className="inspiration-header">
              <div>
                <span className="inspiration-kicker">Prompt Starters</span>
                <h2 id="inspirationTitle">从一个意象开始</h2>
              </div>
              <p className="inspiration-hint">点击卡片，将完整描述带入输入框</p>
            </header>
            <div className="inspiration">
              {ideas.map((idea) => (
                <button
                  className="idea-card"
                  type="button"
                  data-prompt={idea.prompt}
                  key={idea.index}
                  onClick={() => {
                    setPrompt(idea.prompt);
                    composerRef.current?.querySelector("textarea")?.focus();
                    notify("灵感提示词已填入，可继续修改");
                  }}
                >
                  <span className="idea-top"><span className="idea-index">{idea.index}</span><span className="idea-tag">{idea.tag}</span></span>
                  <strong>{idea.title}</strong>
                  <p>{idea.summary}</p>
                  <span className="idea-arrow" aria-hidden="true"><SvgIcon name="external" /></span>
                </button>
              ))}
            </div>
          </section>

          <section className="gallery-section" aria-labelledby="galleryTitle" ref={galleryRef}>
            <div className="gallery-head">
              <div className="tabs" role="tablist" id="galleryTitle">
                <button className={`tab${activeTab === "templates" ? " active" : ""}`} type="button" role="tab" aria-selected={activeTab === "templates"} data-tab="templates" onClick={() => setActiveTab("templates")}>模板</button>
                <button className={`tab${activeTab === "works" ? " active" : ""}`} type="button" role="tab" aria-selected={activeTab === "works"} data-tab="works" onClick={() => setActiveTab("works")}>我的作品</button>
              </div>
              <span className="gallery-note">{galleryNote}</span>
            </div>
            {activeTab === "templates" ? (
              <div className="template-toolbar">
                <div className="template-filters" role="toolbar" aria-label="模板分类">
                  {[
                    ["all", "全部", 4],
                    ["editorial", "人物封面", 1],
                    ["campaign", "视觉海报", 2],
                    ["brand", "品牌菜单", 1],
                  ].map(([key, label, count]) => (
                    <button className={`filter-chip${activeFilter === key ? " active" : ""}`} type="button" data-filter={key} key={key} onClick={() => setActiveFilter(String(key))}>
                      {label}<span>{count}</span>
                    </button>
                  ))}
                </div>
                <span className="template-count">{visibleTemplates.length} 个精选模板</span>
              </div>
            ) : null}
            <div className={`gallery-grid${activeTab !== "templates" ? " gallery-grid--hidden" : ""}`}>
                {visibleTemplates.length ? templateColumns.map((column, colIdx) => (
                  <div className="gallery-column" key={`tpl-col-${colIdx}`}>
                    {column.map((template) => (
                      <article className="art-card" data-kind="template" data-category={template.category} data-template-id={template.id} key={template.id}>
                        <div className={`artwork is-poster ${template.fallbackClass}`} style={{ aspectRatio: ratioToCss(template.aspectRatio) }}>
                          {!hiddenTemplateImages[template.id] ? (
                            <a
                              className="artwork-cover"
                              href={`/hub/ai-image/detail?template=${template.id}`}
                              aria-label={`查看模板 ${template.title}`}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                navigate(`/hub/ai-image/detail?template=${template.id}`);
                              }}
                            >
                              <img src={template.image} alt={template.alt} loading="lazy" onError={() => setHiddenTemplateImages((current) => ({ ...current, [template.id]: true }))} />
                            </a>
                          ) : null}
                          <div className="template-overlay">
                            <div className="overlay-topline">{/* <span className="overlay-label">{template.overlay}</span> */}</div>
                            <div className="template-overlay-actions">
                              <Link
                                className="remake-button"
                                to={`/hub/ai-image/detail?template=${template.id}`}
                                onClick={(event) => { event.preventDefault(); event.stopPropagation(); void applyTemplate(template); }}
                              ><SvgIcon name="sparkles" />做同款</Link>
                              {/* <button className="remake-button download-icon-btn" type="button" onClick={(event) => { event.stopPropagation(); if (template.image) downloadFileSmart(template.image, `ai-template-${template.id}.png`); }}><SvgIcon name="download" /></button> */}
                            </div>
                          </div>
                        </div>
                        <div className="art-body"><h3 className="art-title">{template.title}</h3><div className="meta-row"><span className="meta">{template.meta}</span><span className="meta">{template.aspectRatio}</span></div></div>
                      </article>
                    ))}
                  </div>
                )) : <div className="empty-state visible">当前分类还没有模板。</div>}
              </div>
            <div className={`gallery-grid${activeTab !== "works" ? " gallery-grid--hidden" : ""}`}>
                {visibleWorks.length ? workColumns.map((column, colIdx) => (
                  <div className="gallery-column" key={`work-col-${colIdx}`}>
                    {column.map((work) => (
                      <article className={`art-card work-card${work.pending ? " is-generating" : ""}${work.failed ? " is-failed" : ""}`} data-kind="work" key={work.id}>
                        <div className="artwork" style={{ aspectRatio: ratioToCss(work.aspectRatio) }}>
                          {work.pending ? (
                            <div className="work-status"><span className="work-status-inner"><span className="work-spinner" /><span>正在生成画面</span></span></div>
                          ) : work.failed ? (
                            <div className="work-failed">
                              <div className="work-failed__topline">
                                <div className="work-more-wrap">
                                  <button
                                    type="button"
                                    className="overlay-icon"
                                    aria-label="更多操作"
                                    aria-expanded={openWorkMoreMenuId === work.id}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setOpenWorkMoreMenuId(openWorkMoreMenuId === work.id ? null : work.id);
                                    }}
                                  >
                                    <SvgIcon name="more-horizontal" />
                                  </button>
                                  {openWorkMoreMenuId === work.id ? (
                                    <div className="work-more-popover" onClick={(event) => event.stopPropagation()}>
                                      <Popconfirm
                                        title="确定要删除这个作品吗？"
                                        okText="确定"
                                        cancelText="取消"
                                        placement="topRight"
                                        onPopupClick={(e) => e.stopPropagation()}
                                        onConfirm={() => {
                                          setOpenWorkMoreMenuId(null);
                                          void handleDeleteWork(work);
                                        }}
                                      >
                                        <button
                                          type="button"
                                          className="work-more-item danger"
                                          disabled={!work.taskId || deletingTaskIds.includes(work.taskId)}
                                        >
                                          <SvgIcon name="trash" />
                                          删除作品
                                        </button>
                                      </Popconfirm>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              <span className="work-failed__text">生成失败</span>
                              <div className="work-failed__actions">
                                <button className="work-failed__action" type="button" onClick={() => reuseWork(work)}>再次编辑</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <a
                                className="artwork-cover"
                                href={`/hub/ai-image/detail?work=${encodeURIComponent(String(work.taskId ?? work.id))}&imageIndex=${encodeURIComponent(String(Number(work.id.split("-").pop()) || 0))}`}
                                aria-label={`查看作品 ${work.prompt}`}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  navigate(`/hub/ai-image/detail?work=${encodeURIComponent(String(work.taskId ?? work.id))}&imageIndex=${encodeURIComponent(String(Number(work.id.split("-").pop()) || 0))}`);
                                }}
                              >
                                <img src={work.image} alt={work.prompt} loading="eager" decoding="async" />
                              </a>
                              <div className="template-overlay">
                                <div className="overlay-topline">
                                  <div className="overlay-topline-actions" style={{ marginLeft: "auto" }}>
                                    <div className="work-more-wrap">
                                      <button
                                        type="button"
                                        className="overlay-icon"
                                        aria-label="更多操作"
                                        aria-expanded={openWorkMoreMenuId === work.id}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setOpenWorkMoreMenuId(openWorkMoreMenuId === work.id ? null : work.id);
                                        }}
                                      >
                                        <SvgIcon name="more-horizontal" />
                                      </button>
                                      {openWorkMoreMenuId === work.id ? (
                                        <div className="work-more-popover" onClick={(event) => event.stopPropagation()}>
                                          <Popconfirm
                                            title="确定要删除这个作品吗？"
                                            okText="确定"
                                            cancelText="取消"
                                            placement="topRight"
                                            onPopupClick={(e) => e.stopPropagation()}
                                            onConfirm={() => {
                                              setOpenWorkMoreMenuId(null);
                                              void handleDeleteWork(work);
                                            }}
                                          >
                                            <button
                                              type="button"
                                              className="work-more-item danger"
                                              disabled={!work.taskId || deletingTaskIds.includes(work.taskId)}
                                          >
                                              <SvgIcon name="trash" />
                                              删除作品
                                            </button>
                                          </Popconfirm>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                                <div className="work-overlay-actions">
                                  <Link className="remake-button view-work-button" to={`/hub/ai-image/detail?work=${encodeURIComponent(String(work.taskId ?? work.id))}&imageIndex=${encodeURIComponent(String(Number(work.id.split("-").pop()) || 0))}`}><SvgIcon name="eye" />查看作品</Link>
                                  <button className="remake-button reuse-button" type="button" onClick={() => reuseWork(work)}>再次编辑</button>
                                  <button className="remake-button reuse-button" type="button" onClick={() => work.image && downloadFileSmart(work.image, `ai-image-${work.taskId || work.id}.png`)}><SvgIcon name="download" /></button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="art-body">
                          <h3 className="art-title">{work.pending ? "正在构思新画面..." : makeWorkTitle(work.prompt)}</h3>
                          <div className="meta-row"><span className="meta">{work.params.ratio}</span><span className="meta">{work.params.resolution}</span><span className="meta">{aiImageQualityToLabel(work.params.quality)}</span></div>
                        </div>
                      </article>
                    ))}
                    {/* sentinel 放进最后一列底部，让 IntersectionObserver 持续触发增量加载 */}
                    {colIdx === workColumns.length - 1 && visibleWorks.length < works.length && (
                      <div ref={sentinelRef} style={{ height: 1 }} />
                    )}
                  </div>
                )) : null}
                {works.length === 0 ? <div className="empty-state visible">还没有作品。在上方输入提示词，生成你的第一组画面。</div> : null}
              </div>
          </section>
        </div>
      </div>

      <AppToast config={toastVisible ? { type: toastType, message: toastText, closable: false } : null} onClose={() => setToastVisible(false)} />
    </div>
  );
};
